import React, { useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useSharedValue } from 'react-native-reanimated';

import { Box, Column, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { Modal } from '@/feature/alerts/components/Modal';
import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { BottomSheetModalMethods } from '@gorhom/bottom-sheet/lib/typescript/types';

import { useEvent } from '../../hooks/use-event';
import { Event, Market, Option } from '../../types';
import { Dial } from '../Dial';
import { MarketName } from '../MarketName';
import { OptionButton, getBorderRadiusStyle } from '../OptionButton';
import { AlternateMarketOptions } from './AlternateMarketsModalProvider';

const modalId = 'alternateMarkets';
const GRID_COLUMNS = 2;
const GRID_THRESHOLD = 4;

export type AlternateMarketsModalRef = {
    show: (data: AlternateMarketOptions) => void;
};
/**
 * Modal used to show the alternate markets
 */
export const AlternateMarketsModal = React.forwardRef<AlternateMarketsModalRef, {}>((_props, ref) => {
    const [data, setData] = useState<AlternateMarketOptions | undefined>(undefined);
    const modalRef = useRef<BottomSheetModal>(null);

    useImperativeHandle(ref, () => ({
        show: props => {
            setData(props);
            modalRef.current?.present();
        },
    }));

    return (
        <Modal modalRef={modalRef} id={modalId}>
            {data ? <ModalContent modalRef={modalRef} {...data} /> : null}
        </Modal>
    );
});

const formatDisplayLine = (line: number, marketTypeCode: string) => {
    const isHandicapMarket = marketTypeCode.includes('HANDICAP');
    if (isHandicapMarket) {
        return line > 0 ? `+${line}` : line.toString();
    }
    return line.toString();
};

const ModalContent = ({
    eventId,
    markets,
    modalRef,
    selectedMarket: initialSelectedMarket,
    marketAppendTeamName = false,
}: AlternateMarketOptions & { modalRef: React.RefObject<BottomSheetModalMethods | null> }) => {
    const { data: event } = useEvent(eventId);

    // ! sort markets based on absolute value of the line, rounded up
    const sortedMarkets = useMemo(() => {
        return markets
            .map(market => ({
                ...market,
                line: parseFloat(market.market_type.params?.line?.toString() || '0'),
            }))
            .sort((a, b) => a.line - b.line);
    }, [markets]);

    const [selectedMarketIndex, setSelectedMarketIndex] = useState(
        sortedMarkets.findIndex(market => {
            // ! if initialSelectedMarket is not present return true to match the first index
            if (!initialSelectedMarket) {
                return true;
            }
            return market.id === initialSelectedMarket?.id;
        })
    );

    const selectedMarket = sortedMarkets?.[selectedMarketIndex];

    const marketValues = useMemo(
        () =>
            sortedMarkets?.map(sortedMarket => ({
                title: formatDisplayLine(sortedMarket.line, sortedMarket.market_type.code),
            })),
        [sortedMarkets]
    );

    const scrollValue = useSharedValue(0);

    const onChangeLine = useCallback((index: number) => {
        setSelectedMarketIndex(index);
    }, []);

    const onSelectionAdded = useCallback(() => {
        modalRef?.current?.dismiss();
    }, [modalRef]);

    return (
        <Column>
            <Box height={48} justifyContent="center">
                <MarketName
                    sportName={event?.sport.name ?? ''}
                    textAlign={'center'}
                    variant="titleMedium2"
                    fallback={selectedMarket?.description}
                    marketType={selectedMarket?.market_type}
                    event={event}
                    color={ds.colorToken.text.primary}
                    appendTeamName={marketAppendTeamName}
                    testID="alternateMarketName"
                />
            </Box>

            <Dial
                scrollValue={scrollValue}
                values={marketValues}
                onChangeLine={onChangeLine}
                selectedTab={selectedMarketIndex}
            />

            {event ? (
                <OptionsContent market={selectedMarket} event={event} onSelectionAdded={onSelectionAdded} />
            ) : null}
        </Column>
    );
};

type OptionsContentProps = {
    market: Market;
    event: Event;
    onSelectionAdded: () => void;
};

const OptionsContent = ({ market, event, onSelectionAdded }: OptionsContentProps) => {
    const optionCount = market.options.length;
    const columns = optionCount >= GRID_THRESHOLD ? GRID_COLUMNS : optionCount;
    const totalRows = Math.ceil(optionCount / columns);

    const rows = useMemo(() => {
        const result: Option[][] = [];
        for (let i = 0; i < market.options.length; i += columns) {
            result.push(market.options.slice(i, i + columns));
        }
        return result;
    }, [market.options, columns]);

    return (
        <Column gap="s3" paddingHorizontal="s16" pt="s24" pb="s16">
            {rows.map((rowOptions, rowIndex) => (
                <Row key={`row-${rowIndex}`} gap="s3">
                    {rowOptions.map((option, colIndex) => {
                        const buttonStyle = getBorderRadiusStyle(rowIndex, colIndex, totalRows, columns);
                        return (
                            <OptionButton
                                key={option.id}
                                onSelectionAdded={onSelectionAdded}
                                optionColor={ds.colorToken.background.neutral.subtle}
                                option={option}
                                market={market}
                                event={event}
                                buttonStyle={buttonStyle}
                            />
                        );
                    })}
                </Row>
            ))}
        </Column>
    );
};
